import { useEffect, useRef } from 'react';

interface CompletionRefreshState {
  uid: string | null;
  chatId: string | null;
  enabled: boolean;
  loading: boolean;
  ready: boolean;
  lastBotId: string | null;
}

const SETTLEMENT_REFRESH_DELAYS_MS = [300, 1_700, 6_000];

// Delivery can precede settlement. Coalesce completion into bounded, serial reads.
// Initial history and navigation establish a baseline rather than a completion.
export function useEconomicBillingCompletionRefresh(
  state: CompletionRefreshState,
  refresh: () => Promise<unknown>
) {
  const previous = useRef(state);
  const { uid, chatId, enabled, loading, ready, lastBotId } = state;
  useEffect(() => {
    const prior = previous.current;
    previous.current = { uid, chatId, enabled, loading, ready, lastBotId };
    if (!uid || !enabled || !prior.enabled || prior.uid !== uid || prior.chatId !== chatId) return;
    const completed = prior.loading && !loading;
    const delivered = prior.ready && ready && lastBotId !== null && prior.lastBotId !== lastBotId;
    if (!completed && !delivered) return;
    let canceled = false, index = 0;
    let timer: number;
    const read = async () => {
      try { await refresh(); } catch { /* The controller retains the last server status. */ }
      if (!canceled && ++index < SETTLEMENT_REFRESH_DELAYS_MS.length)
        timer = window.setTimeout(() => { void read(); }, SETTLEMENT_REFRESH_DELAYS_MS[index]);
    };
    timer = window.setTimeout(() => { void read(); }, SETTLEMENT_REFRESH_DELAYS_MS[0]);
    return () => { canceled = true; window.clearTimeout(timer); };
  }, [uid, chatId, enabled, loading, ready, lastBotId, refresh]);
}
